import * as React from 'react';
import { useEffect, useState } from 'react';
import { PlanModel } from '@journeyapps-platform/lib-journey-core';
import { styled } from '../../../../../theme';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoaderSpinner } from '../../../../../widgets/LoaderWidget';
import { ButtonType, ButtonWidget } from '../../../../../widgets/buttons/ButtonWidget';
import { PlanOverviewWidget } from './PlanOveriewWidget';
import { PlanBreakdownWidget } from './PlanBreakdownWidget';
import { Routes, useJourneyNavigation } from '../../../../../routes';
import { PlanOptionsWidget } from './PlanOptionsWidget';

export interface PlanWidgetProps {
  plan: PlanModel;
  className?: any;
}

const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export enum PlanWidgetMode {
  DASHBOARD = 'dashboard',
  ITEMS = 'items'
}

export const PlanWidget: React.FC<PlanWidgetProps> = observer((props) => {
  const [mode, setMode] = useState(PlanWidgetMode.DASHBOARD);
  useEffect(() => {
    props.plan.loadUsage();
    props.plan.loadUpgrades();
  }, []);
  const { navigate, params } = useJourneyNavigation(Routes.MANAGE_PLANS);

  if (!props.plan.usage) {
    return (
      <S.Container className={props.className}>
        <S.PlanDetails>
          <S.TopLeftDetails>
            <S.Label>{props.plan.model.label}</S.Label>
          </S.TopLeftDetails>
        </S.PlanDetails>
        <S.CenterLoader>
          <LoaderSpinner />
        </S.CenterLoader>
      </S.Container>
    );
  }

  const { usage } = props.plan;

  let start = DateTime.fromISO(usage.start);
  let end = DateTime.fromISO(usage.end);
  const billing_enabled = props.plan.model.billing_enabled;

  return (
    <S.Container className={props.className}>
      {props.plan.model.cancellation_requested_at ? (
        <S.CancellationContainer>Processing cancellation request</S.CancellationContainer>
      ) : null}
      <S.PlanDetails>
        <S.TopLeftDetails>
          <S.Label>{props.plan.model.label}</S.Label>
          <S.Period>
            <span>{start.toLocaleString()}</span>
            <S.ArrowIcon icon="arrow-right" />
            <span>{end.toLocaleString()}</span>
          </S.Period>
          <S.Buttons>
            {billing_enabled ? (
              <>
                <ButtonWidget
                  label="Overview"
                  icon="gauge"
                  type={mode === PlanWidgetMode.DASHBOARD ? ButtonType.SECONDARY : ButtonType.TERTIARY}
                  action={() => {
                    setMode(PlanWidgetMode.DASHBOARD);
                  }}
                />
                <ButtonWidget
                  label="Breakdown"
                  icon="table"
                  type={mode === PlanWidgetMode.ITEMS ? ButtonType.SECONDARY : ButtonType.TERTIARY}
                  action={() => {
                    setMode(PlanWidgetMode.ITEMS);
                  }}
                />
                {props.plan.model.billing_enabled ? <PlanOptionsWidget plan={props.plan} /> : null}
              </>
            ) : null}
            {props.plan.upgrades.length > 0 ? (
              <ButtonWidget
                label="Upgrade plan"
                type={ButtonType.PRIMARY}
                action={() => {
                  navigate(Routes.UPGRADE_PLAN, {
                    ...params,
                    plan_id: props.plan.model.id
                  });
                }}
              />
            ) : null}
          </S.Buttons>
        </S.TopLeftDetails>
        {billing_enabled ? (
          <S.TopRightDetails>
            <S.PriceLabel>Total</S.PriceLabel>
            <S.PriceTotal>{formatter.format(usage.total / 100.0)}</S.PriceTotal>
          </S.TopRightDetails>
        ) : null}
      </S.PlanDetails>
      {mode === PlanWidgetMode.DASHBOARD ? (
        <PlanOverviewWidget plan={props.plan} />
      ) : (
        <PlanBreakdownWidget plan={props.plan} />
      )}
    </S.Container>
  );
});
namespace S {
  export const Buttons = styled.div`
    margin-left: 20px;
    display: flex;
    align-items: center;
    column-gap: 5px;
  `;

  export const CenterLoader = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;
  `;

  export const Container = styled.div`
    background: ${(p) => p.theme.dialog.background};
    border: solid 2px rgba(255, 255, 255, 0.2);
    box-shadow: 0 0 40px rgba(0, 0, 0, 0.5);
    padding: 30px;
    border-radius: 10px;
  `;

  export const PlanDetails = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 10px;
  `;

  export const PriceLabel = styled.div`
    opacity: 0.3;
    color: ${(p) => p.theme.text.primary};
    font-size: 17px;
  `;

  export const PriceTotal = styled.div`
    color: ${(p) => p.theme.text.primary};
    font-size: 30px;
  `;

  export const TopLeftDetails = styled.div`
    display: flex;
    align-items: center;
    flex-grow: 1;
  `;

  export const TopRightDetails = styled.div`
    display: flex;
    align-items: flex-end;
    flex-direction: column;
  `;

  export const Period = styled.div`
    margin-left: 50px;
    color: ${(p) => p.theme.text.primary};
    font-size: 17px;
    display: flex;
    align-items: center;
  `;

  export const Label = styled.div`
    color: ${(p) => p.theme.text.primary};
    font-size: 25px;
  `;

  export const ArrowIcon = styled(FontAwesomeIcon)`
    margin-left: 10px;
    margin-right: 10px;
  `;

  export const CancellationContainer = styled.div`
    background: ${(p) => p.theme.beta.background};
    color: ${(p) => p.theme.beta.foreground};
    display: inline-block;
    padding: 5px 10px;
    border-radius: 5px;
  `;
}
